import React, { useState } from 'react';
import { useVideoUpload } from '../../../context/VideoUploadContext';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const GlobalUploadProgress = () => {
  const { activeUploads, removeUpload, cancelUpload } = useVideoUpload();
  const [isMinimized, setIsMinimized] = useState(false);
  
  const uploadList = Object.values(activeUploads);
  if (uploadList.length === 0) return null;

  const activeCount = uploadList.filter(u => ['initializing', 'uploading', 'confirming'].includes(u.status)).length;
  const completedCount = uploadList.filter(u => u.status === 'completed').length;
  const errorCount = uploadList.filter(u => u.status === 'error').length;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-80 shadow-2xl rounded-xl border border-slate-200 bg-white overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
      {/* Header */}
      <div className="bg-slate-900 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {activeCount > 0 ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
          ) : errorCount > 0 ? (
            <AlertCircle className="w-4 h-4 text-rose-400" />
          ) : (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          )}
          <span className="text-sm font-medium">
            {activeCount > 0 ? `Subiendo ${activeCount} vídeo${activeCount > 1 ? 's' : ''}...` : 
             errorCount > 0 ? 'Error en la subida' : 'Subida completada'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-slate-700 rounded transition-colors"
          >
            {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <div className="max-h-80 overflow-y-auto p-4 space-y-4">
          {uploadList.map((upload) => (
            <div key={upload.id} className="space-y-2 relative group">
              <div className="flex justify-between items-start pr-6">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate" title={upload.filename}>
                    {upload.filename}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {upload.status === 'uploading' && `Enviando a S3... ${upload.progress}%`}
                    {upload.status === 'initializing' && 'Iniciando subida...'}
                    {upload.status === 'confirming' && 'Confirmando con servidor...'}
                    {upload.status === 'completed' && '¡Completado! AWS empezará a procesar.'}
                    {upload.status === 'error' && (
                      <span className="text-rose-600 font-medium">Error: {upload.error}</span>
                    )}
                  </p>
                </div>
                {(upload.status === 'uploading' || upload.status === 'initializing') ? (
                  <button
                    onClick={() => cancelUpload(upload.id)}
                    className="absolute top-0 right-0 px-1.5 py-0.5 text-[10px] font-medium text-rose-600 hover:bg-rose-50 rounded bg-white border border-rose-100"
                    title="Cancelar subida"
                  >
                    Cancelar
                  </button>
                ) : upload.status !== 'confirming' ? (
                  <button
                    onClick={() => removeUpload(upload.id)}
                    className="absolute top-0 right-0 p-1 text-slate-400 hover:text-slate-600 rounded bg-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                ) : null}
              </div>

              {/* Progress Bar */}
              {(upload.status === 'uploading' || upload.status === 'confirming' || upload.status === 'initializing') && (
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      upload.status === 'confirming' ? 'bg-emerald-500 animate-pulse' : 'bg-blue-600'
                    }`}
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}

              {upload.status === 'completed' && (
                <div className="h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
                  <div className="h-full w-full bg-emerald-500" />
                </div>
              )}
              
              {upload.status === 'error' && (
                <div className="h-1.5 w-full bg-rose-100 rounded-full overflow-hidden">
                  <div className="h-full w-full bg-rose-500" />
                </div>
              )}
            </div>
          ))}
          
          <p className="text-[10px] text-slate-400 text-center pt-2 italic">
            Puedes seguir navegando por el ERP mientras se suben los archivos.
          </p>
        </div>
      )}

      {isMinimized && activeCount > 0 && (
        <div className="h-1 w-full bg-slate-800">
          <div 
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${Math.max(...uploadList.map(u => u.progress || 0))}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default GlobalUploadProgress;
